import React, { useEffect, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Petit badge affiché dans l'en-tête d'un rapport pour indiquer si le client
 * a déjà ouvert le rapport via un lien public partagé.
 *
 * Props:
 *   rapportType: 'ticfe' | 'ticgn' | 'turpe' | 'turpe_multi'
 *   rapportId: string
 */
const _fmtRelative = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `il y a ${Math.floor(diff / 86400)} j`;
  return d.toLocaleDateString('fr-FR');
};

export default function ShareAccessBadge({ rapportType, rapportId }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!rapportType || !rapportId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(
          `${API_URL}/api/share/list?rapport_type=${rapportType}&rapport_id=${rapportId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!r.ok) return;
        const shares = await r.json();
        if (cancelled) return;

        if (!shares || shares.length === 0) {
          setSummary({ hasShares: false });
          return;
        }
        const totalViews = shares.reduce((s, x) => s + (x.access_count_view || 0), 0);
        const totalPdfs = shares.reduce((s, x) => s + (x.access_count_pdf || 0), 0);
        const lastAccessed = shares
          .map((x) => x.last_accessed_at)
          .filter(Boolean)
          .sort()
          .pop();
        setSummary({
          hasShares: true,
          opened: totalViews + totalPdfs > 0,
          totalViews,
          totalPdfs,
          lastAccessed,
        });
      } catch {
        // silently ignore — pas critique
      }
    })();
    return () => { cancelled = true; };
  }, [rapportType, rapportId]);

  if (!summary || !summary.hasShares) return null;

  if (!summary.opened) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200"
        title="Un lien de partage a été créé mais le client ne l'a pas encore ouvert."
        data-testid="share-access-badge-pending"
      >
        <EyeOff className="w-3.5 h-3.5" />
        Lien envoyé · Pas encore consulté
      </span>
    );
  }

  const totalAccess = (summary.totalViews || 0) + (summary.totalPdfs || 0);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200"
      title={`Vues: ${summary.totalViews} • PDF: ${summary.totalPdfs} • Dernière ouverture: ${summary.lastAccessed ? new Date(summary.lastAccessed).toLocaleString('fr-FR') : ''}`}
      data-testid="share-access-badge-opened"
    >
      <Eye className="w-3.5 h-3.5" />
      Consulté {totalAccess > 1 ? `${totalAccess} fois` : 'par le client'} · {_fmtRelative(summary.lastAccessed)}
    </span>
  );
}
